import type { RouteLocationNormalizedLoaded } from 'vue-router';

type RouteInput = Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>;

function pathWithQuery(path: string, query: RouteLocationNormalizedLoaded['query']): string {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
        const values = Array.isArray(value) ? value : [value];
        for (const entry of values) {
            if (typeof entry === 'string' && entry.length > 0) {
                search.append(key, entry);
            }
        }
    }

    const queryString = search.toString();
    return queryString ? `${path}?${queryString}` : path;
}

function routeId(params: RouteLocationNormalizedLoaded['params'], key: 'estateId' | 'mfhPortfolioId'): number {
    const raw = params[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    const id = Number(value);

    if (!Number.isSafeInteger(id) || id <= 0) {
        throw new Error(`Invalid ${key} for RAM sign-off embed`);
    }

    return id;
}

export function rootSignOffRamEmbedProps(route: RouteInput) {
    return {
        cockpitFeature: 'sign-off-envelopes-root-overview' as const,
        persistenceKey: 'global-approvals',
        pathAndSearch: pathWithQuery('/approvals', route.query),
        title: 'Approvals (RAM)',
    };
}

export function rootSignOffCreateRamEmbedProps(route: RouteInput) {
    return {
        cockpitFeature: 'sign-off-envelopes-root-create' as const,
        persistenceKey: 'global-approvals-create',
        pathAndSearch: pathWithQuery('/approvals/create', route.query),
        title: 'Create approval (RAM)',
    };
}

function rootEnvelopeId(params: RouteLocationNormalizedLoaded['params']): string {
    const raw = params.signOffEnvelopeId;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && /^\d+$/.test(value)) {
        return value;
    }
    return '';
}

export function rootSignOffDetailRamEmbedProps(route: RouteInput) {
    const envelopeId = rootEnvelopeId(route.params);
    const path = envelopeId !== '' ? `/approvals/${envelopeId}` : '/approvals';

    return {
        cockpitFeature: 'sign-off-envelopes-root-detail' as const,
        persistenceKey: envelopeId !== '' ? `global-approval-${envelopeId}` : 'global-approvals',
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Approval detail (RAM)',
    };
}

export function rootSignOffAdditionalDocumentRamEmbedProps(route: RouteInput) {
    const envelopeId = rootEnvelopeId(route.params);
    const path = envelopeId !== '' ? `/approvals/${envelopeId}/additional-document` : '/approvals';

    return {
        cockpitFeature: 'sign-off-envelopes-root-additional-document' as const,
        persistenceKey: envelopeId !== '' ? `global-approval-additional-${envelopeId}` : 'global-approvals',
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Additional documents (RAM)',
    };
}

export function portfolioSignOffRamEmbedProps(route: RouteInput) {
    const mfhPortfolioId = routeId(route.params, 'mfhPortfolioId');
    const path = `/sourcing/mfh-portfolios/${mfhPortfolioId}/approvals`;

    return {
        cockpitFeature: 'sign-off-envelopes-portfolio-overview' as const,
        persistenceKey: mfhPortfolioId,
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Approvals (RAM)',
        wrapInCard: true,
    };
}

export function portfolioSignOffCreateRamEmbedProps(route: RouteInput) {
    const mfhPortfolioId = routeId(route.params, 'mfhPortfolioId');

    return {
        cockpitFeature: 'sign-off-envelopes-portfolio-create' as const,
        persistenceKey: mfhPortfolioId,
        pathAndSearch: pathWithQuery(`/sourcing/mfh-portfolios/${mfhPortfolioId}/approvals/create`, route.query),
        title: 'Create approval (RAM)',
        wrapInCard: true,
    };
}

export function portfolioSignOffDetailRamEmbedProps(route: RouteInput) {
    const mfhPortfolioId = routeId(route.params, 'mfhPortfolioId');
    const envelopeId = rootEnvelopeId(route.params);
    const path =
        envelopeId !== '' ? `/sourcing/mfh-portfolios/${mfhPortfolioId}/approvals/${envelopeId}` : `/sourcing/mfh-portfolios/${mfhPortfolioId}/approvals`;

    return {
        cockpitFeature: 'sign-off-envelopes-portfolio-detail' as const,
        persistenceKey: mfhPortfolioId,
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Approval detail (RAM)',
        wrapInCard: true,
    };
}

export function estateSignOffRamEmbedProps(route: RouteInput) {
    const estateId = routeId(route.params, 'estateId');
    const path = `/units/${estateId}/approvals`;

    return {
        cockpitFeature: 'sign-off-envelopes-estate-overview' as const,
        persistenceKey: estateId,
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Approvals (RAM)',
    };
}

export function estateSignOffCreateRamEmbedProps(route: RouteInput) {
    const estateId = routeId(route.params, 'estateId');

    return {
        cockpitFeature: 'sign-off-envelopes-estate-create' as const,
        persistenceKey: estateId,
        pathAndSearch: pathWithQuery(`/units/${estateId}/approvals/create`, route.query),
        title: 'Create approval (RAM)',
    };
}

export function estateSignOffDetailRamEmbedProps(route: RouteInput) {
    const estateId = routeId(route.params, 'estateId');
    const envelopeId = rootEnvelopeId(route.params);
    const path = envelopeId !== '' ? `/units/${estateId}/approvals/${envelopeId}` : `/units/${estateId}/approvals`;

    return {
        cockpitFeature: 'sign-off-envelopes-estate-detail' as const,
        persistenceKey: estateId,
        pathAndSearch: pathWithQuery(path, route.query),
        title: 'Approval detail (RAM)',
    };
}
